// FileHistoryLens — one material's past: versions, diff, restore.
//
// The per-resource depth the timeline's `edited_material` row drills into
// (view-file-history-diff-restore.md). Three states in one panel:
//   1. Timeline — versions newest-first, "Current" marked.
//   2. Preview — selecting a past version shows a persistent "Viewing Version 7
//      · read-only" banner (no inferred mode — Figma's documented pitfall) with
//      a primary Restore + a Back-to-current escape.
//   3. Compare — the net diff (selected vs Current) rendered element-grain by
//      the shipped `InlineDiff`, via the pure `diffOpShim` (so the shim is
//      exercised in the render path).
//
// Restore is forward-only and REVERSIBLE, and the confirm says so + names the
// one real consequence (pending proposals dismissed — D7). This lens does NOT
// dispatch: `onRestore(marker)` is a prop the integrator wires to the real
// `restore_resource_version` command. Pure/props; the only state is ephemeral
// view-state (whether the confirm is open).

export type { FileHistoryLensProps };
export { FileHistoryLens };

import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Button } from "../../Button/Button.js";
import { InlineDiff } from "../../Editor/InlineDiff/InlineDiff.js";
import { EmptyState } from "../../EmptyState/EmptyState.js";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import { ActorChip } from "../actor.js";
import { type DiffOpLabels, toUiDiffOp } from "../diffOpShim.js";
import type { DiffChange, VersionDiff, VersionEntry } from "./types.js";
import "./FileHistory.css";

type FileHistoryLensProps = {
    readonly resourceTitle: string;
    // Versions newest-first; exactly one carries `isCurrent`.
    readonly versions: readonly VersionEntry[];
    // The version under preview (a past one), or undefined for "current".
    readonly selectedVersionId?: string;
    readonly onSelectVersion?: (id: string) => void;
    // The net diff of the selected version against current. Present only in
    // preview; the shell folds + diffs and hands it in.
    readonly diff?: VersionDiff;
    // How many pending assistant suggestions a restore would dismiss (D7). The
    // confirm counts them BEFORE the click — never a silent side effect.
    readonly pendingProposalCount?: number;
    // The restore handler — the integrator wires it to the domain command.
    readonly onRestore?: (marker: string) => void;
    readonly onBackToCurrent?: () => void;
};

const FileHistoryLens = ({
    resourceTitle,
    versions,
    selectedVersionId,
    onSelectVersion,
    diff,
    pendingProposalCount = 0,
    onRestore,
    onBackToCurrent,
}: FileHistoryLensProps): ReactNode => {
    const { t } = useLingui();
    const [confirming, setConfirming] = useState(false);

    if (versions.length === 0) {
        return (
            <EmptyState
                icon={<Icon name="clock" size="lg" />}
                title={<Trans>No versions yet</Trans>}
                body={<Trans>This material's edits will appear here.</Trans>}
            />
        );
    }

    const selected = versions.find((v) => v.id === selectedVersionId);
    const inPreview = selected !== undefined && !selected.isCurrent;

    // Localise the diff op's machine seams for the shim. Unknown field tokens
    // (attr keys) pass through — better a raw key than a wrong guess.
    const labels: DiffOpLabels = {
        field: (token) => {
            switch (token) {
                case "text":
                    return t`text`;
                case "type":
                    return t`kind`;
                default:
                    return token;
            }
        },
        position: (index) => t`position ${index + 1}`,
    };

    return (
        <div className="bilda-hi-fh">
            <Text as="p" size="sm" tone="dim">
                <Trans>Version history for {resourceTitle}</Trans>
            </Text>

            {inPreview && selected !== undefined && (
                <div className="bilda-hi-fh__banner" role="status">
                    <span className="bilda-hi-fh__banner-text">
                        <Icon name="clock" size="sm" />
                        <Text as="span" size="sm" weight="medium">
                            <Trans>
                                Viewing {selected.versionLabel} · read-only
                            </Trans>
                        </Text>
                    </span>
                    <span className="bilda-hi-fh__banner-actions">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setConfirming(false);
                                onBackToCurrent?.();
                            }}
                        >
                            <Trans>Back to current</Trans>
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setConfirming(true)}
                        >
                            <Trans>Restore this version</Trans>
                        </Button>
                    </span>
                </div>
            )}

            {inPreview && confirming && selected !== undefined && (
                <RestoreConfirm
                    versionLabel={selected.versionLabel}
                    when={selected.when}
                    pendingProposalCount={pendingProposalCount}
                    onConfirm={() => {
                        onRestore?.(selected.id);
                        setConfirming(false);
                    }}
                    onCancel={() => setConfirming(false)}
                />
            )}

            <div className="bilda-hi-fh__cols">
                <ul className="bilda-hi-fh__versions">
                    {versions.map((version) => (
                        <VersionRow
                            key={version.id}
                            version={version}
                            selected={version.id === selectedVersionId}
                            onSelect={
                                onSelectVersion
                                    ? () => {
                                          setConfirming(false);
                                          onSelectVersion(version.id);
                                      }
                                    : undefined
                            }
                        />
                    ))}
                </ul>

                {inPreview && diff !== undefined && (
                    <div className="bilda-hi-fh__diff">
                        <Text as="p" size="xs" tone="dim">
                            <Trans>
                                What restoring would change ({diff.fromLabel} →{" "}
                                {diff.toLabel})
                            </Trans>
                        </Text>
                        {diff.changes.length === 0 ? (
                            <Text as="p" size="sm" tone="dim">
                                <Trans>No differences.</Trans>
                            </Text>
                        ) : (
                            <ul className="bilda-hi-fh__changes">
                                {diff.changes.map((change) => (
                                    <ChangeRow
                                        key={change.id}
                                        change={change}
                                        labels={labels}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// One version in the timeline — selectable; the current one is marked, not a
// door. `aria-current` carries the "you are here" to AT.
const VersionRow = ({
    version,
    selected,
    onSelect,
}: {
    version: VersionEntry;
    selected: boolean;
    onSelect?: () => void;
}): ReactNode => {
    const inner = (
        <>
            <span className="bilda-hi-fh__version-head">
                <Text as="span" size="sm" weight="medium">
                    {version.versionLabel}
                </Text>
                {version.isCurrent && (
                    <Text as="span" size="xs" tone="accent">
                        <Trans>Current</Trans>
                    </Text>
                )}
            </span>
            <Text as="span" size="xs" tone="dim">
                {version.summary}
            </Text>
            <span className="bilda-hi-fh__version-meta">
                <ActorChip actor={version.actor} />
                <Text as="span" size="xs" tone="faint">
                    {version.when}
                </Text>
            </span>
        </>
    );
    return (
        <li>
            {onSelect && !version.isCurrent ? (
                <button
                    type="button"
                    className="bilda-hi-fh__version"
                    aria-pressed={selected}
                    onClick={onSelect}
                >
                    {inner}
                </button>
            ) : (
                <div
                    className="bilda-hi-fh__version bilda-hi-fh__version--static"
                    aria-current={version.isCurrent ? "true" : undefined}
                >
                    {inner}
                </div>
            )}
        </li>
    );
};

// One diff change, rendered by the shipped InlineDiff via the shim. add/remove
// wrap the element body; scalar-edit/move render from the op alone.
const ChangeRow = ({
    change,
    labels,
}: {
    change: DiffChange;
    labels: DiffOpLabels;
}): ReactNode => (
    <li className="bilda-hi-fh__change">
        <Text as="p" size="xs" tone="faint">
            {change.elementLabel}
        </Text>
        <InlineDiff op={toUiDiffOp(change.op, labels)}>
            {change.body}
        </InlineDiff>
    </li>
);

// The consequence-naming confirm (D7): names the target, promises reversibility,
// discloses the one real side effect (proposals dismissed) BEFORE the click.
const RestoreConfirm = ({
    versionLabel,
    when,
    pendingProposalCount,
    onConfirm,
    onCancel,
}: {
    versionLabel: string;
    when: string;
    pendingProposalCount: number;
    onConfirm: () => void;
    onCancel: () => void;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <div
            className="bilda-hi-fh__confirm"
            role="alertdialog"
            aria-label={t`Confirm restore`}
        >
            <Text as="p" size="sm">
                <Trans>
                    Restore to {versionLabel} ({when})? Your current version is
                    kept in history — you can switch back anytime.
                </Trans>
            </Text>
            {pendingProposalCount > 0 && (
                <Text as="p" size="sm" tone="warning">
                    <Trans>
                        {pendingProposalCount} pending assistant suggestions
                        will be dismissed.
                    </Trans>
                </Text>
            )}
            <div className="bilda-hi-fh__confirm-actions">
                <Button variant="ghost" size="sm" onClick={onCancel}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button variant="primary" size="sm" onClick={onConfirm}>
                    <Trans>Restore</Trans>
                </Button>
            </div>
        </div>
    );
};
